import { Card } from '@yleisradio/areena-types';
import React, { useEffect, useRef, useState } from 'react';
import logger from 'services/logger';
import { trackCardView } from 'services/snowplowAnalytics/snowplowAnalytics';
import { findLabel } from 'utils/card';
import { GroupEngagementData } from './types';
import { ViewModeType } from 'services/snowplowAnalytics/contextGenerators';

type Props = {
  card: Card;
  cardIndex: number;
  children?: React.ReactNode;
  groupData?: GroupEngagementData;
  viewUuid: string;
  viewMode: ViewModeType;
  wrapperLabel?: string | undefined;
  metaItemId?: string | undefined;
};

export const ViewableCard: React.FC<Props> = ({
  metaItemId,
  card,
  cardIndex,
  children,
  groupData,
  viewUuid,
  viewMode,
  wrapperLabel,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isSent, setSent] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || isSent) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry || !entry.isIntersecting) return;

      const entityId = getEntityIdForCard(card);

      if (!entityId) {
        logger.error({ card }, 'No entityId found for card');
        return;
      }
      const pageloadAnalytics = card.controls?.[cardIndex]?.analytics?.pageload;
      const packageId =
        pageloadAnalytics?.yle?.entity_type === 'areena_package'
          ? pageloadAnalytics?.yle?.id
          : undefined;

      trackCardView({
        cardIndex,
        cardTitle: card.title,
        cardViewUuid: viewUuid,
        entityId,
        imageId: card.image?.id,
        groupData,
        packageId,
        viewMode,
        wrapperLabel,
      });
      setSent(true);
    });

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [
    card,
    cardIndex,
    groupData,
    isSent,
    viewUuid,
    viewMode,
    wrapperLabel,
    metaItemId,
  ]);

  return <div ref={ref}>{children}</div>;
};

function getEntityIdForCard(card: Card): string | undefined {
  return (
    findLabel(card.labels, 'itemId')?.raw ||
    card.persistentState?.id ||
    findLabel(card.labels, 'broadcastService')?.raw
  );
}
